import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, IconButton, TextField, Button, Chip, Tooltip,
    Paper, Dialog, DialogTitle, DialogContent, DialogActions,
    CircularProgress, InputAdornment, Fade
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import AddIcon from '@mui/icons-material/Add';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CloseIcon from '@mui/icons-material/Close';
import {
    notasListarJWT, notasCrearJWT, notasEditarJWT,
    notasToggleFijarJWT, notasEliminarJWT
} from '../../api/endpoints';
import { parseFechaServer, tiempoRelativo } from '../../utils/fechas';

const COLORES = [
    { id: 'amarillo', bg: '#fff9c4', border: '#f9e547', header: '#f5e100' },
    { id: 'azul', bg: '#e3f2fd', border: '#90caf9', header: '#42a5f5' },
    { id: 'verde', bg: '#e8f5e9', border: '#a5d6a7', header: '#66bb6a' },
    { id: 'naranja', bg: '#fff3e0', border: '#ffcc80', header: '#ffa726' },
    { id: 'rojo', bg: '#ffebee', border: '#ef9a9a', header: '#ef5350' },
    { id: 'violeta', bg: '#f3e5f5', border: '#ce93d8', header: '#ab47bc' },
];

const getColor = (id) => COLORES.find(c => c.id === id) || COLORES[0];

const EMPTY_NOTA = { descripcion: '', texto: '', color: 'amarillo', recordatorio: '' };

function NotasTab({ idExpediente }) {
    const [notas, setNotas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busqueda, setBusqueda] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editData, setEditData] = useState(EMPTY_NOTA);
    const [editId, setEditId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const cargar = useCallback(async () => {
        try {
            const resp = await notasListarJWT(idExpediente);
            if (resp?.ok) setNotas(resp.notas || []);
        } catch { }
        finally { setLoading(false); }
    }, [idExpediente]);

    useEffect(() => { cargar(); }, [cargar]);

    // --- CRUD ---
    const handleNueva = () => {
        setEditId(null);
        setEditData({ ...EMPTY_NOTA });
        setDialogOpen(true);
    };

    const handleEditar = (nota) => {
        setEditId(nota.id);
        setEditData({
            descripcion: nota.descripcion || '',
            texto: nota.texto || '',
            color: nota.color || 'amarillo',
            recordatorio: nota.recordatorio ? nota.recordatorio.substring(0, 16) : '',
        });
        setDialogOpen(true);
    };

    const handleGuardar = async () => {
        if (!editData.descripcion.trim()) return;
        setSaving(true);
        try {
            const payload = {
                ...editData,
                recordatorio: editData.recordatorio || null,
            };
            if (editId) {
                await notasEditarJWT(idExpediente, editId, payload);
            } else {
                await notasCrearJWT(idExpediente, payload);
            }
            setDialogOpen(false);
            await cargar();
        } catch { }
        finally { setSaving(false); }
    };

    const handleFijar = async (id) => {
        await notasToggleFijarJWT(idExpediente, id);
        await cargar();
    };

    const handleEliminar = async () => {
        if (!confirmDelete) return;
        await notasEliminarJWT(idExpediente, confirmDelete);
        setConfirmDelete(null);
        await cargar();
    };

    // --- Filtrado ---
    const notasFiltradas = busqueda.trim()
        ? notas.filter(n =>
            (n.descripcion || '').toLowerCase().includes(busqueda.toLowerCase()) ||
            (n.texto || '').toLowerCase().includes(busqueda.toLowerCase())
        )
        : notas;

    if (loading) {
        return <Box display="flex" justifyContent="center" py={6}><CircularProgress size={32} /></Box>;
    }

    return (
        <Box>
            {/* Header */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleNueva}
                    size="small"
                    sx={{
                        textTransform: 'none', fontWeight: 600, borderRadius: 1,
                        boxShadow: 'none', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }
                    }}
                >
                    Nueva nota
                </Button>
                {notas.length > 3 && (
                    <TextField
                        size="small"
                        placeholder="Buscar en notas..."
                        value={busqueda}
                        onChange={e => setBusqueda(e.target.value)}
                        sx={{ minWidth: 220 }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#bbb' }} /></InputAdornment>,
                        }}
                    />
                )}
            </Box>

            {/* Grid de cards */}
            {notasFiltradas.length === 0 ? (
                <Box textAlign="center" py={6}>
                    <Typography variant="body1" sx={{ color: '#bbb', fontSize: '1rem' }}>
                        {notas.length === 0 ? 'No tenés notas en este expediente' : 'Sin resultados para la búsqueda'}
                    </Typography>
                    {notas.length === 0 && (
                        <Typography variant="body2" sx={{ color: '#ccc', mt: 1 }}>
                            Las notas son personales, solo vos las podés ver
                        </Typography>
                    )}
                </Box>
            ) : (
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                    gap: 2,
                }}>
                    {notasFiltradas.map((nota, idx) => {
                        const c = getColor(nota.color);
                        return (
                            <Fade in key={nota.id} timeout={300 + idx * 50}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        border: `1.5px solid ${c.border}`,
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        transition: 'box-shadow 0.2s, transform 0.2s',
                                        '&:hover': {
                                            boxShadow: `0 4px 16px ${c.border}66`,
                                            transform: 'translateY(-2px)',
                                        },
                                    }}
                                >
                                    {/* Header de la card */}
                                    <Box sx={{
                                        backgroundColor: c.header,
                                        px: 1.5, py: 0.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        minHeight: 36,
                                    }}>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <Tooltip title={nota.fijada ? 'Desfijar' : 'Fijar arriba'}>
                                                <IconButton size="small" onClick={() => handleFijar(nota.id)}
                                                    sx={{ color: '#fff', p: 0.25 }}>
                                                    {nota.fijada ? <PushPinIcon sx={{ fontSize: 16 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 16 }} />}
                                                </IconButton>
                                            </Tooltip>
                                            {nota.fijada && (
                                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600, fontSize: '0.65rem' }}>
                                                    FIJADA
                                                </Typography>
                                            )}
                                        </Box>
                                        <Box display="flex" alignItems="center" gap={0.25}>
                                            {nota.recordatorio && !nota.recordatorioEnviado && (
                                                <Tooltip title={`Recordatorio: ${new Date(nota.recordatorio).toLocaleString('es-AR')}`}>
                                                    <NotificationsActiveIcon sx={{ fontSize: 15, color: '#fff', mr: 0.5 }} />
                                                </Tooltip>
                                            )}
                                            <Tooltip title="Editar">
                                                <IconButton size="small" onClick={() => handleEditar(nota)} sx={{ color: '#fff', p: 0.25 }}>
                                                    <EditIcon sx={{ fontSize: 15 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Eliminar">
                                                <IconButton size="small" onClick={() => setConfirmDelete(nota.id)} sx={{ color: 'rgba(255,255,255,0.8)', p: 0.25 }}>
                                                    <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>

                                    {/* Body */}
                                    <Box sx={{ backgroundColor: c.bg, px: 2, py: 1.5, flexGrow: 1 }}>
                                        <Typography variant="body2" sx={{
                                            fontWeight: 600, color: '#333', mb: 0.5,
                                            fontSize: '0.85rem', lineHeight: 1.4,
                                            display: '-webkit-box', WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                        }}>
                                            {nota.descripcion}
                                        </Typography>
                                        {nota.texto && (
                                            <Typography variant="body2" sx={{
                                                color: '#666', fontSize: '0.78rem', lineHeight: 1.4, mt: 0.5,
                                                display: '-webkit-box', WebkitLineClamp: 4,
                                                WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                                whiteSpace: 'pre-wrap',
                                            }}>
                                                {nota.texto}
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Footer */}
                                    <Box sx={{
                                        backgroundColor: c.bg, px: 2, py: 0.75,
                                        borderTop: `1px solid ${c.border}40`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    }}>
                                        <Tooltip title={nota.fecha ? new Date(nota.fecha).toLocaleString('es-AR') : ''}>
                                            <Typography variant="caption" sx={{ color: '#999', fontSize: '0.7rem' }}>
                                                {tiempoRelativo(nota.fecha)}
                                            </Typography>
                                        </Tooltip>
                                        {nota.recordatorio && (
                                            <Chip
                                                size="small"
                                                icon={nota.recordatorioEnviado
                                                    ? <NotificationsNoneIcon sx={{ fontSize: '14px !important' }} />
                                                    : <NotificationsActiveIcon sx={{ fontSize: '14px !important' }} />
                                                }
                                                label={new Date(nota.recordatorio).toLocaleDateString('es-AR')}
                                                sx={{
                                                    height: 20, fontSize: '0.65rem',
                                                    backgroundColor: nota.recordatorioEnviado ? '#eee' : '#fff3e0',
                                                    color: nota.recordatorioEnviado ? '#999' : '#e65100',
                                                    '& .MuiChip-icon': { color: nota.recordatorioEnviado ? '#999' : '#e65100' },
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Paper>
                            </Fade>
                        );
                    })}
                </Box>
            )}

            {/* Dialog crear/editar */}
            <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: 1 } }}>
                <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>
                    {editId ? 'Editar nota' : 'Nueva nota'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        {/* Color picker */}
                        <Box>
                            <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, mb: 0.5, display: 'block' }}>
                                Color
                            </Typography>
                            <Box display="flex" gap={1}>
                                {COLORES.map(c => (
                                    <Tooltip key={c.id} title={c.id.charAt(0).toUpperCase() + c.id.slice(1)}>
                                        <Box
                                            onClick={() => setEditData(d => ({ ...d, color: c.id }))}
                                            sx={{
                                                width: 32, height: 32, borderRadius: '50%',
                                                backgroundColor: c.header, cursor: 'pointer',
                                                border: editData.color === c.id ? '3px solid #333' : '3px solid transparent',
                                                transition: 'transform 0.15s',
                                                '&:hover': { transform: 'scale(1.15)' },
                                            }}
                                        />
                                    </Tooltip>
                                ))}
                            </Box>
                        </Box>

                        {/* Título */}
                        <TextField
                            label="Titulo / Descripcion *"
                            value={editData.descripcion}
                            onChange={e => setEditData(d => ({ ...d, descripcion: e.target.value }))}
                            fullWidth
                            size="small"
                            inputProps={{ maxLength: 100, autoComplete: 'on' }}
                            name="sigif-nota-descripcion"
                            autoFocus
                        />

                        {/* Texto detalle */}
                        <TextField
                            label="Detalle (opcional)"
                            value={editData.texto || ''}
                            onChange={e => setEditData(d => ({ ...d, texto: e.target.value }))}
                            fullWidth
                            size="small"
                            multiline
                            minRows={3}
                            maxRows={8}
                            inputProps={{ maxLength: 2000 }}
                        />

                        {/* Recordatorio */}
                        <Box>
                            <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, mb: 0.5, display: 'block' }}>
                                Recordatorio (opcional)
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1}>
                                {/* DateTimePicker con locale es: usa formato dd/MM/yyyy HH:mm
                                    independientemente del locale del navegador. Sin esto, en
                                    Chrome con SO en inglés se ve MM/DD. */}
                                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                                    <DateTimePicker
                                        value={parseFechaServer(editData.recordatorio)}
                                        onChange={(d) => {
                                            // Guardamos como string compatible con el back (datetime-local format)
                                            if (!d || isNaN(d.getTime())) {
                                                setEditData(prev => ({ ...prev, recordatorio: '' }));
                                            } else {
                                                const yyyy = d.getFullYear();
                                                const mm = String(d.getMonth() + 1).padStart(2, '0');
                                                const dd = String(d.getDate()).padStart(2, '0');
                                                const hh = String(d.getHours()).padStart(2, '0');
                                                const mi = String(d.getMinutes()).padStart(2, '0');
                                                setEditData(prev => ({ ...prev, recordatorio: `${yyyy}-${mm}-${dd}T${hh}:${mi}` }));
                                            }
                                        }}
                                        format="dd/MM/yyyy HH:mm"
                                        ampm={false}
                                        slotProps={{ textField: { size: 'small', sx: { flex: 1 } } }}
                                    />
                                </LocalizationProvider>
                                {editData.recordatorio && (
                                    <Tooltip title="Quitar recordatorio">
                                        <IconButton size="small" onClick={() => setEditData(d => ({ ...d, recordatorio: '' }))}>
                                            <CloseIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                            <Typography variant="caption" sx={{ color: '#aaa', mt: 0.5, display: 'block' }}>
                                Recibirás una notificación en la campanita cuando llegue la fecha
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancelar</Button>
                    <Button variant="contained" onClick={handleGuardar} disabled={saving || !editData.descripcion.trim()}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1 }}>
                        {saving ? <CircularProgress size={20} /> : (editId ? 'Guardar' : 'Crear nota')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm delete */}
            <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 600 }}>Eliminar nota</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">¿Estás seguro de que querés eliminar esta nota?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
                    <Button color="error" variant="contained" onClick={handleEliminar}
                        sx={{ textTransform: 'none', borderRadius: 1 }}>
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default NotasTab;
